import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Loader2, Upload, Palette, Pen, Plus, Trash2, ShoppingBag, Layers, Star, Gift } from "lucide-react";
import CardDesigner from "@/components/CardDesigner";
import { API } from "@/lib/api";
import { DEFAULT_BRAND_COLOR, DEFAULT_ON_BRAND, brand } from "@/lib/brand";


const DEFAULT_TIERS = [
  { name: "Bronze", min_points: 0, reward_value_gbp: 0, perks: "" },
  { name: "Silver", min_points: 500, reward_value_gbp: 5, perks: "" },
  { name: "Gold", min_points: 1500, reward_value_gbp: 15, perks: "" },
];
function tiersFromConfig(cfg) {
  if (!cfg || !cfg.length) return DEFAULT_TIERS.map(t => ({ ...t }));
  return cfg.map(t => ({ ...t, perks: Array.isArray(t.perks) ? t.perks.join(", ") : (t.perks || "") }));
}

function RewardSelect({ rewards, value, onChange, testid }) {
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger className="h-8 text-sm" data-testid={testid}><SelectValue placeholder="Select reward" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">— No reward —</SelectItem>
        {rewards.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function VendorDesignPage() {
  const { authHeaders, user } = useAuth();
  const navigate = useNavigate();
  const vid = user?.vendor_id;
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [tiers, setTiers] = useState(DEFAULT_TIERS.map(t => ({ ...t })));
  const [stampRewards, setStampRewards] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [logoUploading, setLogoUploading] = useState(null);
  const [brandColor, setBrandColor] = useState(DEFAULT_BRAND_COLOR);
  const [textColor, setTextColor] = useState(DEFAULT_ON_BRAND);
  const [bodyTextColor, setBodyTextColor] = useState(brand.color.line);
  const [cardLayout, setCardLayout] = useState(null);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [allowSignup, setAllowSignup] = useState(true);

  const load = async () => {
    if (!vid) return;
    try {
      const [{ data }, rewardsRes] = await Promise.all([
        axios.get(`${API}/vendors/${vid}`, { headers: authHeaders() }),
        axios.get(`${API}/rewards/${vid}`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      ]);
      setRewards((rewardsRes.data || []).filter(r => r.enabled !== false));
      setVendor(data);
      setEditForm(data);
      setTiers(tiersFromConfig(data.tier_config));
      setBrandColor(data.brand_color || DEFAULT_BRAND_COLOR);
      setTextColor(data.text_color || DEFAULT_ON_BRAND);
      setBodyTextColor(data.body_text_color || brand.color.line);
      setCardLayout(data.card_layout || null);
      setAllowSignup(data.allow_customer_signup !== false);
      setStampRewards(data.stamp_rewards?.length > 0
        ? data.stamp_rewards.map(s => ({ ...s }))
        : [{ orders_required: 5, reward_id: "" }, { orders_required: 10, reward_id: "" }]
      );
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [vid]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...editForm, allow_customer_signup: allowSignup };
    if (editForm?.reward_logic === "tier_based") {
      body.tier_config = tiers.map(t => ({
        name: t.name,
        min_points: parseInt(t.min_points, 10) || 0,
        reward_id: t.reward_id || "",
        perks: t.perks ? t.perks.split(",").map(p => p.trim()).filter(Boolean) : [],
      }));
    }
    if (editForm?.reward_logic === "order_based") {
      body.stamp_rewards = stampRewards.map(s => ({
        orders_required: parseInt(s.orders_required, 10) || 1,
        reward_id: s.reward_id || "",
      }));
      body.stamp_min_spend = parseFloat(editForm.stamp_min_spend) || 0;
      body.stamp_max_per_day = parseInt(editForm.stamp_max_per_day, 10) || 0;
      body.stamp_auto_reset = editForm.stamp_auto_reset !== false;
    }
    if (editForm?.reward_logic === "spend_based" || editForm?.reward_logic === "item_based") {
      body.reward_threshold_reward_id = editForm.reward_threshold_reward_id || "";
    }
    try {
      await axios.put(`${API}/vendors/${vid}`, body, { headers: authHeaders() });
      await load();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleBrandingSave = async () => {
    setBrandingSaving(true);
    try {
      await axios.put(`${API}/vendors/${vid}`, { brand_color: brandColor, text_color: textColor, body_text_color: bodyTextColor, card_layout: cardLayout }, { headers: authHeaders() });
      await load();
    } catch (err) { console.error(err); }
    finally { setBrandingSaving(false); }
  };

  const handleLogoUpload = async (e, logoType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(logoType);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/vendors/${vid}/upload-logo?logo_type=${logoType}`, formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (err) { alert(err.response?.data?.detail || "Upload failed"); }
    finally { setLogoUploading(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-ink/40"><Loader2 size={20} className="animate-spin" /></div>;
  if (!vendor) return null;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="vendor-design-page">
      <div>
        <h1 className="text-2xl font-bold text-ink tracking-tight">Design</h1>
        <p className="text-sm text-ink/40 mt-0.5">Configure your reward system, branding and wallet pass</p>
      </div>

      {/* Wallet Pass Designer — top-level CTA */}
      <Card className="bg-white border-0 shadow-sm rounded-2xl" data-testid="wallet-designer-card">
        <CardContent className="px-5 py-4 flex items-center gap-5">
          <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center shrink-0">
            <Pen size={22} className="text-ink" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-ink">Wallet Pass Designer</p>
            <p className="text-xs text-ink/40 mt-0.5">Design your Apple &amp; Google Wallet card — choose pass style, colours, fields, stamp icons and more.</p>
          </div>
          <Button onClick={() => navigate(`/vendors/${vid}/wallet-designer`)} className="bg-purple-500 hover:bg-purple-600 text-white gap-1.5 rounded-full shrink-0" data-testid="open-wallet-designer-btn">
            <Pen size={13} /> Open Designer
          </Button>
        </CardContent>
      </Card>

      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings" data-testid="tab-card-settings">Card Settings</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
        </TabsList>

        {/* CARD SETTINGS TAB */}
        <TabsContent value="settings" className="mt-4">
          <Card className="bg-white border-0 shadow-sm rounded-2xl">
            <CardContent className="px-5 py-5">
              <form onSubmit={handleSave} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-ink/60">Business Name</Label>
                    <Input value={editForm?.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-widest text-ink/60">Industry</Label>
                    <Input value={editForm?.industry || ""} onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-ink/60">Reward System</Label>
                  <Select value={editForm?.reward_logic || "spend_based"} onValueChange={(v) => {
                    setEditForm({ ...editForm, reward_logic: v });
                    if (v === "tier_based" && tiers.length === 0) setTiers(DEFAULT_TIERS.map(t => ({ ...t })));
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spend_based">Points for Money Spent</SelectItem>
                      <SelectItem value="order_based">Stamp Card (per Order)</SelectItem>
                      <SelectItem value="tier_based">Tiered Rewards</SelectItem>
                      <SelectItem value="item_based">Item-based</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(editForm?.reward_logic === "spend_based" || editForm?.reward_logic === "item_based") && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-widest text-ink/60">Points per £1</Label><Input type="number" value={editForm?.points_per_pound || 10} onChange={(e) => setEditForm({ ...editForm, points_per_pound: e.target.value })} /></div>
                      <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-widest text-ink/60">Threshold (pts)</Label><Input type="number" value={editForm?.reward_threshold || 500} onChange={(e) => setEditForm({ ...editForm, reward_threshold: e.target.value })} /></div>
                    </div>
                    <div className="p-4 bg-purple-50 border border-ink/10 rounded-xl space-y-2">
                      <div className="flex items-center gap-2"><Gift size={14} className="text-ink" /><p className="text-sm font-semibold text-ink">Reward at threshold</p></div>
                      {rewards.length === 0 ? (
                        <p className="text-xs text-ink/40">No rewards yet — create one in the <button type="button" onClick={() => navigate("/my-rewards")} className="underline text-ink/70">Rewards</button> page first.</p>
                      ) : (
                        <RewardSelect rewards={rewards} value={editForm?.reward_threshold_reward_id} onChange={(v) => setEditForm({ ...editForm, reward_threshold_reward_id: v })} testid="threshold-reward-select" />
                      )}
                    </div>
                  </div>
                )}

                {editForm?.reward_logic === "order_based" && (
                  <div className="p-4 bg-purple-50 border border-ink/10 rounded-xl space-y-3">
                    <div className="flex items-center gap-2">
                      <ShoppingBag size={14} className="text-ink" />
                      <p className="text-sm font-semibold text-ink">Stamp Card Milestones</p>
                    </div>
                    {rewards.length === 0 && (
                      <p className="text-xs text-ink/40">No rewards yet — create one in the <button type="button" onClick={() => navigate("/my-rewards")} className="underline text-ink/70">Rewards</button> page first.</p>
                    )}
                    {stampRewards.map((sr, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center p-3 bg-white border border-ink/10 rounded-lg">
                        <div className="col-span-3">
                          <p className="text-[10px] text-ink/40 mb-1">Stamps</p>
                          <Input className="h-8 text-sm" type="number" min="1" placeholder="5" value={sr.orders_required} onChange={(e) => { const u = [...stampRewards]; u[i] = { ...u[i], orders_required: e.target.value }; setStampRewards(u); }} data-testid={`milestone-stamps-${i}`} />
                        </div>
                        <div className="col-span-8">
                          <p className="text-[10px] text-ink/40 mb-1">Reward</p>
                          <RewardSelect rewards={rewards} value={sr.reward_id} onChange={(v) => { const u = [...stampRewards]; u[i] = { ...u[i], reward_id: v }; setStampRewards(u); }} testid={`milestone-reward-${i}`} />
                        </div>
                        <button type="button" className="col-span-1 flex justify-center items-end pb-1.5 text-ink/30 hover:text-danger" onClick={() => setStampRewards(stampRewards.filter((_, idx) => idx !== i))}><Trash2 size={14} /></button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => setStampRewards([...stampRewards, { orders_required: "", reward_id: "" }])} className="h-7 text-xs gap-1" data-testid="add-milestone-btn"><Plus size={12} /> Add Milestone</Button>

                    <div className="pt-2 border-t border-ink/10 space-y-3">
                      <p className="text-xs font-semibold uppercase tracking-widest text-ink/50">Stamp Rules</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[10px] text-ink/50">Min. Qualifying Spend (£)</Label>
                          <Input type="number" min="0" step="0.01" value={editForm?.stamp_min_spend ?? 0} onChange={(e) => setEditForm({ ...editForm, stamp_min_spend: e.target.value })} className="h-8 text-sm" data-testid="stamp-min-spend" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] text-ink/50">Max Stamps / Day (0 = no limit)</Label>
                          <Input type="number" min="0" value={editForm?.stamp_max_per_day ?? 0} onChange={(e) => setEditForm({ ...editForm, stamp_max_per_day: e.target.value })} className="h-8 text-sm" data-testid="stamp-max-day" />
                        </div>
                      </div>
                      <div className="flex items-center justify-between bg-white border border-ink/10 rounded-lg px-3 py-2">
                        <div>
                          <p className="text-sm font-medium text-ink">Auto-reset stamps</p>
                          <p className="text-[10px] text-ink/40">Reset to 0 after the final milestone is reached</p>
                        </div>
                        <Switch checked={editForm?.stamp_auto_reset !== false} onCheckedChange={(v) => setEditForm({ ...editForm, stamp_auto_reset: v })} data-testid="stamp-auto-reset" />
                      </div>
                    </div>
                  </div>
                )}

                {editForm?.reward_logic === "tier_based" && (
                  <div className="p-4 bg-purple-50 border border-ink/10 rounded-xl space-y-3">
                    <div className="flex items-center gap-2"><Layers size={14} className="text-ink" /><p className="text-sm font-semibold text-ink">Tier Configuration</p></div>
                    {rewards.length === 0 && (
                      <p className="text-xs text-ink/40">No rewards yet — create one in the <button type="button" onClick={() => navigate("/my-rewards")} className="underline text-ink/70">Rewards</button> page first.</p>
                    )}
                    {tiers.map((t, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 bg-white border border-ink/10 rounded-lg">
                        <div className="col-span-3">
                          <p className="text-[10px] text-ink/40 mb-1">Tier name</p>
                          <Input className="h-8 text-sm" value={t.name} onChange={(e) => { const u = [...tiers]; u[i] = { ...u[i], name: e.target.value }; setTiers(u); }} placeholder="Gold" />
                        </div>
                        <div className="col-span-2">
                          <p className="text-[10px] text-ink/40 mb-1">Min pts</p>
                          <Input className="h-8 text-sm" type="number" value={t.min_points} onChange={(e) => { const u = [...tiers]; u[i] = { ...u[i], min_points: e.target.value }; setTiers(u); }} placeholder="500" />
                        </div>
                        <div className="col-span-3">
                          <p className="text-[10px] text-ink/40 mb-1">Reward</p>
                          <RewardSelect rewards={rewards} value={t.reward_id} onChange={(v) => { const u = [...tiers]; u[i] = { ...u[i], reward_id: v }; setTiers(u); }} testid={`tier-reward-${i}`} />
                        </div>
                        <div className="col-span-3">
                          <p className="text-[10px] text-ink/40 mb-1">Perks (comma-sep)</p>
                          <Input className="h-8 text-sm" value={t.perks || ""} onChange={(e) => { const u = [...tiers]; u[i] = { ...u[i], perks: e.target.value }; setTiers(u); }} placeholder="Free delivery" />
                        </div>
                        <button type="button" className="col-span-1 flex justify-center pb-1.5 text-ink/30 hover:text-danger" onClick={() => setTiers(tiers.filter((_, idx) => idx !== i))}><Trash2 size={14} /></button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={() => setTiers([...tiers, { name: "", min_points: 0, reward_id: "", perks: "" }])} className="h-7 text-xs gap-1" data-testid="add-tier-btn"><Plus size={12} /> Add Tier</Button>
                  </div>
                )}

                <div className="flex items-center justify-between p-3 bg-purple-50 border border-ink/10 rounded-xl">
                  <div>
                    <p className="text-sm font-medium text-ink">Customer Self-Registration</p>
                    <p className="text-xs text-ink/40 mt-0.5">Allow customers to sign up at <span className="font-mono text-ink/60">/{vendor?.slug}/join</span></p>
                  </div>
                  <Switch checked={allowSignup} onCheckedChange={setAllowSignup} data-testid="allow-signup-toggle" />
                </div>

                <Button type="submit" disabled={saving} className="gap-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-full">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Settings
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BRANDING TAB */}
        <TabsContent value="branding" className="mt-4">
          <Card className="bg-white border-0 shadow-sm rounded-2xl">
            <CardContent className="px-5 py-5 space-y-6">
              {/* Logos */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-ink/60 mb-3">Logos</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {["header", "icon"].map(logoType => (
                    <div key={logoType} className="border border-ink/10 rounded-xl p-4">
                      <p className="text-sm font-medium text-ink capitalize mb-2">{logoType === "header" ? "Banner Image" : "Icon (Square)"}</p>
                      {vendor?.[`logo_${logoType}_path`] && (
                        <img src={`${API}/vendors/${vid}/logo/${logoType}?t=${Date.now()}`} alt="" className="h-16 object-contain rounded mb-2 bg-surface" />
                      )}
                      <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-ink/15 text-xs font-medium text-ink/60 hover:text-ink cursor-pointer">
                        {logoUploading === logoType ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                        {logoUploading === logoType ? "Uploading..." : "Upload"}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoUpload(e, logoType)} />
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Colors */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-ink/60 mb-3">Colors</p>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[
                    { label: "Card Background", val: brandColor, set: setBrandColor },
                    { label: "Primary Text", val: textColor, set: setTextColor },
                    { label: "Body Text", val: bodyTextColor, set: setBodyTextColor },
                  ].map(c => (
                    <div key={c.label} className="flex items-center gap-3">
                      <input type="color" value={c.val} onChange={(e) => c.set(e.target.value)} className="w-8 h-8 rounded-lg border border-ink/10 cursor-pointer p-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-ink/50">{c.label}</p>
                        <Input value={c.val} onChange={(e) => c.set(e.target.value)} className="h-7 text-xs font-mono mt-0.5" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Card Designer */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-ink/60 mb-3">Card Layout</p>
                <CardDesigner layout={cardLayout} onLayoutChange={setCardLayout} />
              </div>

              <div className="flex justify-end pt-2">
                <Button onClick={handleBrandingSave} disabled={brandingSaving} className="bg-purple-500 hover:bg-purple-600 text-white gap-1.5 rounded-full">
                  {brandingSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Branding
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
